const API_URL = import.meta.env.VITE_API_URL ?? '';

interface ApiError {
  error: string;
  message: string;
}

function getToken(): string | null {
  return localStorage.getItem('platform_admin_token');
}

function setToken(token: string) {
  localStorage.setItem('platform_admin_token', token);
}

function clearToken() {
  localStorage.removeItem('platform_admin_token');
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${path}`, { ...options, headers });

  if (response.status === 401) {
    clearToken();
    if (!window.location.pathname.includes('/admin/login')) {
      window.location.href = '/admin/login';
    }
    const error: ApiError = await response.json().catch(() => ({
      error: 'Unauthorized',
      message: 'Session expired. Please log in again.',
    }));
    throw error;
  }

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      error: 'Error',
      message: 'Something went wrong.',
    }));
    throw error;
  }

  return response.json();
}

export const adminApi = {
  login(email: string, password: string) {
    return request<{
      token: string;
      admin: { id: string; email: string; name: string };
      churches: Array<{ id: string; name: string; slug: string }>;
    }>(
      '/api/v1/admin/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    );
  },

  impersonate(churchId: string) {
    return request<{ token: string; churchId: string; churchName: string }>(
      '/api/v1/admin/auth/impersonate',
      { method: 'POST', body: JSON.stringify({ churchId }) },
    );
  },

  getMe() {
    return request<{ id: string; email: string; name: string }>('/api/v1/admin/auth/me');
  },

  getSystemStats() {
    return request<{ churches: number; users: number; submissions: number }>('/api/v1/admin/stats');
  },

  getChurches() {
    return request<Array<{
      id: string;
      name: string;
      slug: string;
      denomination: string | null;
      timezone: string;
      createdAt: string;
      userCount: number;
    }>>('/api/v1/admin/churches');
  },

  getChurchStats(churchId: string) {
    return request<{ users: number; submissions: number; services: number; songs: number }>(
      `/api/v1/admin/churches/${churchId}/stats`,
    );
  },

  getChurchUsers(churchId: string, params?: Record<string, string>) {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return request<{
      data: Array<{
        id: string;
        email: string;
        name: string;
        phone: string | null;
        avatarUrl: string | null;
        createdAt: string;
        lastActiveAt: string | null;
        roles: string[];
      }>;
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/api/v1/admin/churches/${churchId}/users${query}`);
  },

  updateUserRoles(churchId: string, userId: string, roles: string[]) {
    return request<{ userId: string; roles: string[] }>(
      `/api/v1/admin/churches/${churchId}/users/${userId}/roles`,
      { method: 'PATCH', body: JSON.stringify({ roles }) },
    );
  },

  softDeleteUser(churchId: string, userId: string) {
    return request<{ userId: string; deletedAt: string }>(
      `/api/v1/admin/churches/${churchId}/users/${userId}`,
      { method: 'DELETE' },
    );
  },

  setToken,
  clearToken,
  getToken,
};
